import {useSyncExternalStore} from "react";
import {toMs} from "@cdx/shared/duration";
import type {Account} from "../cdx-models/Account";
import {ExternalStore} from "./ExternalStore";
import {getLocalStorageVal, setLocalStorageVal} from "./storage";
import {countWorkdays, diffDays, setToMidnight} from "./date-utils";
import {pluralize} from "./utils";

export type TimeFormatPreference = "12h" | "24h" | null;

const locale = ["en-GB", "en-US"];

export const detectDeviceUses12Hour = (): boolean => {
  try {
    // resolvedOptions() only reports an hour cycle when the format includes an hour field.
    const {hourCycle, hour12} = Intl.DateTimeFormat(undefined, {hour: "numeric"}).resolvedOptions();
    if (hourCycle) return hourCycle === "h11" || hourCycle === "h12";
    if (typeof hour12 === "boolean") return hour12;
  } catch {
    // fall back to 24h
  }
  return false;
};

// Seeded from localStorage so a returning user's first paint already uses their preference.
class Uses12HourStore extends ExternalStore<boolean> {
  private deviceUses12Hour = detectDeviceUses12Hour();
  private pref: TimeFormatPreference = getLocalStorageVal("time-format", null);
  private uses12Hour = this.resolve();

  private resolve() {
    return this.pref ? this.pref === "12h" : this.deviceUses12Hour;
  }

  getSnapshot = () => this.uses12Hour;

  setPreference(pref: TimeFormatPreference) {
    if (pref === this.pref) return;
    this.pref = pref;
    setLocalStorageVal("time-format", pref);
    const next = this.resolve();
    if (next === this.uses12Hour) return;
    this.uses12Hour = next;
    this.notify();
  }
}

const uses12HourStore = new Uses12HourStore();

export const setTimeFormatPreference = (pref: TimeFormatPreference) =>
  uses12HourStore.setPreference(pref);

const dateFormatters = {
  /** "3 Mar 2025" */
  day: new Intl.DateTimeFormat(locale, {year: "numeric", month: "short", day: "numeric"}),
  /** "3 Mar"; "3 Mar 2025" in another year */
  dayShort: new Intl.DateTimeFormat(locale, {month: "short", day: "numeric"}),
  /** "Monday 3 Mar"; "3 Mar 2025" in another year */
  dayWithWeekday: new Intl.DateTimeFormat(locale, {
    weekday: "long",
    month: "short",
    day: "numeric",
  }),
  /** "Mon 3" */
  weekdayDay: new Intl.DateTimeFormat(locale, {weekday: "short", day: "numeric"}),
  /** "Monday" */
  weekday: new Intl.DateTimeFormat(locale, {weekday: "long"}),
  /** "March" */
  month: new Intl.DateTimeFormat(locale, {month: "long"}),
  /** "Mar" */
  monthShort: new Intl.DateTimeFormat(locale, {month: "short"}),
  /** "Mar 2025" */
  monthYear: new Intl.DateTimeFormat(locale, {month: "short", year: "numeric"}),
  /** "Mar 25" */
  monthYearShort: new Intl.DateTimeFormat(locale, {month: "short", year: "2-digit"}),
};

export type DateFormat = keyof typeof dateFormatters;

const formatDateAt = (date: Date, format: DateFormat, now: Date) => {
  const yearOnlyIfOther = format === "dayShort" || format === "dayWithWeekday";
  if (yearOnlyIfOther && date.getFullYear() !== now.getFullYear()) {
    return dateFormatters.day.format(date);
  }
  return dateFormatters[format].format(date);
};

/** A date without a clock time; see `dateFormatters` for each format's output. */
export const formatDate = (date: Date, format: DateFormat) =>
  formatDateAt(date, format, new Date());

const SIX_MONTHS_MS = toMs.days(6 * 30);

const createDateTimeFormat = (uses12Hour: boolean) => {
  const hourCycle = uses12Hour ? "h12" : "h23";
  const dateTime = new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hourCycle,
  });
  const full = new Intl.DateTimeFormat(locale, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    hourCycle,
  });
  return {
    /** "3 Mar, 14:05" */
    dateTime: (date: Date) => dateTime.format(date),
    /** Like `dateTime`, but "3 Mar 2024" for dates in another year and over six months ago. */
    smartDateTime: (date: Date) => {
      const now = new Date();
      const showYear =
        date.getFullYear() !== now.getFullYear() && now.getTime() - date.getTime() > SIX_MONTHS_MS;
      return showYear ? dateFormatters.day.format(date) : dateTime.format(date);
    },
    /** "Monday, 3 March 2025 at 14:05:09", for title attributes */
    full: (date: Date) => full.format(date),
    /** Hour 0-24 of a day: "9:00" / "9 am". 24 is midnight at the end of the day. */
    hour: (hour: number) => {
      if (!uses12Hour) return `${hour}:00`;
      const h24 = hour % 24;
      return `${h24 % 12 || 12} ${h24 < 12 ? "am" : "pm"}`;
    },
  };
};

export type DateTimeFormat = ReturnType<typeof createDateTimeFormat>;

const formats = new Map<boolean, DateTimeFormat>();
const getDateTimeFormat = (uses12Hour: boolean) => {
  let format = formats.get(uses12Hour);
  if (!format) {
    format = createDateTimeFormat(uses12Hour);
    formats.set(uses12Hour, format);
  }
  return format;
};

/** Formatters for clock times. The component re-renders only when the 12h/24h choice flips. */
export const useDateTimeFormat = (): DateTimeFormat =>
  getDateTimeFormat(useSyncExternalStore(uses12HourStore.subscribe, uses12HourStore.getSnapshot));

export type RelativeOptions = {
  /** "for 3 days" / "until tomorrow" instead of "in 3 days" / "tomorrow", e.g. for a snooze */
  future?: "in" | "for";
  /** Days (workdays when `workdays` is set) after which the date itself is shown ("on 3 Mar 2025") */
  maxDays?: number;
  /** Compare calendar days only: "today" instead of "5m ago" / "3h ago" */
  unit?: "day";
  /** Count days beyond yesterday/tomorrow as the account's workdays: "in 3 workdays" */
  workdays?: Account["workdays"];
  /** "today", "3d", "-3d", then "3 Mar" — for narrow columns. Always day-based. */
  style?: "short";
  /** Name the weekday ("Friday") for future dates fewer than this many calendar days away */
  weekdayWithin?: number;
};

/** "just now", "5m ago", "yesterday", "in 3 days" — relative to `now`, which stays untouched. */
export const formatRelative = (
  date: Date,
  now: Date,
  {
    future = "in",
    unit,
    workdays,
    style,
    maxDays = style === "short" ? 11 : 31,
    weekdayWithin = 0,
  }: RelativeOptions = {}
) => {
  const diffInS = (date.getTime() - now.getTime()) / 1000;
  const isFuture = diffInS > 0;
  const absS = Math.abs(diffInS);
  const nowMidnight = setToMidnight(new Date(now));
  const dayDiff = diffDays(setToMidnight(new Date(date)), nowMidnight);
  const wrap = (amount: string) => (isFuture ? `${future} ${amount}` : `${amount} ago`);

  if (unit === "day" || style === "short") {
    if (dayDiff === 0) return "today";
  } else {
    if (absS < 60 && !(isFuture && future === "for")) return isFuture ? "right now" : "just now";
    if (absS < 60 * 60) return wrap(`${Math.max(1, Math.floor(absS / 60))}m`);
    if (absS < 60 * 60 * 12 || dayDiff === 0) return wrap(`${Math.floor(absS / (60 * 60))}h`);
  }
  if (style !== "short") {
    switch (dayDiff) {
      case -1:
        return "yesterday";
      case 1:
        return future === "for" ? "until tomorrow" : "tomorrow";
    }
    if (dayDiff > 0 && dayDiff < weekdayWithin) {
      const weekday = dateFormatters.weekday.format(date);
      return future === "for" ? `until ${weekday}` : weekday;
    }
  }

  // A date on a weekend can be zero workdays away; calendar days read better than "0 workdays".
  const workdayCount = workdays ? countWorkdays(workdays, nowMidnight, dayDiff) : 0;
  const count = workdayCount || dayDiff;
  const unitWord = workdayCount ? "workday" : "day";

  if (style === "short") {
    if (Math.abs(count) < maxDays) return `${count}d`;
    return formatDateAt(date, "dayShort", now);
  }
  if (Math.abs(count) < maxDays) return wrap(pluralize(Math.abs(count), unitWord));
  const day = dateFormatters.day.format(date);
  return isFuture && future === "for" ? `until ${day}` : `on ${day}`;
};
